import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

import MultipartFileUpload from '../Workflows/MultipartFileUpload';

const UnifiedUpload = ({ open, onClose, onUploadComplete }) => {
  const [uploadType, setUploadType] = useState('');
  const [property, setProperty] = useState('');
  const [properties, setProperties] = useState([]);

  // Fetch properties data
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const token = localStorage.getItem('token');
        const tenantId = localStorage.getItem('tenant_id');
        
        if (!token || !tenantId) {
          console.error('Authentication token or tenant ID not found');
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${localStorage.getItem('user_id')}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch properties: ${response.status}`);
        }

        const propertiesData = await response.json();
        setProperties(propertiesData);
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    };

    if (open) {
      // Reset dropdowns when opening dialog
      setUploadType('');
      setProperty('');
      fetchProperties();
    }
  }, [open]);

  const handleCloseDialog = () => {
    onClose();
  };

  const handleUploadComplete = (results) => {
    // Add upload type and property selection to results
    const enhancedResults = results.map(result => ({
      ...result,
      additional_details: {
        uploadType: uploadType,
        property: property
      }
    }));
    
    if (onUploadComplete) {
      onUploadComplete(enhancedResults);
    }
    
    // Close dialog after successful upload
    handleCloseDialog();
  };

  const getDialogTitle = () => {
    return uploadType === 'GL' ? 'GL Upload' : uploadType === 'Bank' ? 'Bank Upload' : 'Upload';
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '95vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2 }}>
        <Box>
          <Typography variant="h6" component="div" sx={{ fontWeight: 500 }}>
            {getDialogTitle()}
          </Typography>
        </Box>
        <IconButton aria-label="close" onClick={handleCloseDialog}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ p: 3 }}>
        {/* Upload Options Section */}
        <Box sx={{ mb: 3, p: 2 }}>
          {/* <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#000000' }}>
            Select Upload Options
          </Typography> */}
          
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {/* Upload Type Dropdown */}
            <FormControl sx={{ minWidth: 200, flex: 1, mt: 1 }} required>
              <Typography variant="body2" sx={{ 
                fontWeight: 500,
                color: '#000000',
                fontSize: '0.75rem',
                mb: 1
              }}>
                Upload Type
              </Typography>
              <Autocomplete
                options={[
                  { label: 'GL', value: 'GL' },
                  { label: 'Bank', value: 'Bank' }
                ]}
                getOptionLabel={(option) => option.label}
                value={uploadType ? { label: uploadType, value: uploadType } : null}
                onChange={(event, newValue) => {
                  setUploadType(newValue ? newValue.value : '');
                  setProperty(''); // Reset property when upload type changes
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Upload Type"
                    size="small"
                    sx={{ 
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#d0d0d0',
                      }
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                sx={{ fontSize: '14px' }}
              />
            </FormControl>

            {/* Property Dropdown */}
            <FormControl sx={{ minWidth: 200, flex: 1, mt: 1 }} required>
              <Typography variant="body2" sx={{ 
                fontWeight: 500, 
                color: '#000000',
                fontSize: '0.75rem',
                mb: 1
              }}>
                Property
              </Typography>
              <Autocomplete
                options={properties}
                getOptionLabel={(option) => {
                  const code = option.propertyCode || option.PROPERTY_CODE || '';
                  const name = option.propertyName || option.PROPERTY_NAME || '';
                  return code && name ? `${code} - ${name}` : name;
                }}
                value={properties.find(p => (p.propertyName || p.PROPERTY_NAME) === property) || null}
                onChange={(event, newValue) => {
                  setProperty(newValue ? (newValue.propertyName || newValue.PROPERTY_NAME) : '');
                }}
                disabled={!uploadType}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Property"
                    size="small"
                    sx={{ 
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#d0d0d0',
                      }
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => 
                  (option.propertyName || option.PROPERTY_NAME) === (value?.propertyName || value?.PROPERTY_NAME)
                }
                sx={{ fontSize: '14px' }}
              />
            </FormControl>
          </Box>
        </Box>

        {/* File Upload Component */}
        <Box sx={{ mt: 2 }}>
          <MultipartFileUpload 
            onClose={handleCloseDialog}
            onUploadComplete={handleUploadComplete}
            category="GL"
            additionalDetails={{
              uploadType: uploadType,
              property: property
            }}
            disabled={!uploadType || !property}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UnifiedUpload;
